import { useSetState } from 'minimal-shared/hooks';
import { useMemo, useEffect, useCallback } from 'react';
import { signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { getErrorMessage } from 'src/auth/utils';

import { AuthContext } from '../auth-context';
import { setSession, isValidToken, clearAllAuthData } from './utils';

import type { AuthState } from '../../types';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({ user: null, loading: true });
  const router = useRouter();

  const logout = useCallback(async () => {
    try {
      await signOut();
      clearAllAuthData();

      setState({ user: null, loading: false });
      router.replace(paths.auth.amplify.signIn);
    } catch (error) {
      getErrorMessage(error);
      clearAllAuthData();

      setState({ user: null, loading: false });
      router.replace(paths.auth.amplify.signIn);
    }
  }, [setState, router]);

  const checkUserSession = useCallback(async () => {
    try {
      const session = await fetchAuthSession();

      if (session.tokens?.accessToken) {
        const accessTokenString = session.tokens.accessToken.toString();
        const idTokenString = session.tokens.idToken?.toString();

        if (!isValidToken(accessTokenString)) {
          await logout();
          return;
        }
        await setSession(accessTokenString, logout);

        const user = await getCurrentUser();

        setState({
          user: {
            id: user.userId,
            email: user.signInDetails?.loginId || '',
            username: user.username,
            accessToken: accessTokenString,
            idToken: idTokenString,
            role: 'admin',
          },
          loading: false,
        });
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      getErrorMessage(error);
      await logout();
    }
  }, [setState, logout]);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      checkUserSession,
      logout,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [checkUserSession, logout, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
